import React, { useEffect, useState, useMemo } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  Button,
  Select,
  FormHelperText,
  useToast,
  List,
  ListItem,
  Heading,
} from "@chakra-ui/react";

import { useForm, Controller } from "react-hook-form";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { thousands_separators } from "../utils/formatCurrency";
import emailjs, { init } from "emailjs-com";

import { Datepicker } from "./Datepicker";
import { format } from "date-fns";
import { useBanks, useProduce, useProduceTypes } from "../hooks/useProduceType";
import {
  useHandlers,
  useOrganisationBanks,
  useSuppliers,
} from "../hooks/useSuppliers";
import { SOURCING_PRODUCTS } from "../api/urls";
import { useMutation } from "react-query";
import { useAxios } from "../hooks/useAxios";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";

init("fjSCWIyYgVH0htxvU");

const Form = () => {
  const queryClient = useQueryClient();
  const loggedInUser = queryClient.getQueryData("user");
  console.log("Logged in user:", loggedInUser);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    getValues,
    setValue,
    control,
  } = useForm({
    mode: "onBlur",
  });
  const { data: products } = useProduce();

  const [data, setData] = useState({});
  const [startDate, setStartDate] = useState(new Date());
  const [harvestDate, setHarvestDate] = useState(new Date());

  const toast = useToast();
  const axios = useAxios();
  const history = useHistory();

  const { data: supplier } = useSuppliers();
  const { data: handlers } = useHandlers();
  const { data: productTypes } = useProduceTypes();
  const { data: banks } = useBanks();

  const watchedCost = watch("cost_per_kg");
  const watchedWeight = watch("total_net_weight");
  const totalCost = parseFloat(watchedCost) * parseFloat(watchedWeight);

  const selectedSupplier = supplier?.find(
    (supp) => supp.id === watch("supplier_id"),
  );
  const selectedHandler = handlers?.find(
    (hand) => hand.id === watch("served_by_id"),
  );
  const selectedProduct = productTypes?.find(
    (prod) => prod.id == watch("product_type_id"),
  );

  const { data: userBanks } = useOrganisationBanks(selectedSupplier?.id);
  const chickenPartsProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((p) => Number(p.product_type?.id) === 9);
  }, [products]);
  const othertypeProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((p) => Number(p.product_type?.id) === 3);
  }, [products]);

  useEffect(() => {
    if (userBanks?.length > 0) {
      setValue("payment_name", userBanks[0]?.bank?.name);
      setValue("payment_number", userBanks[0]?.account_number);
    }
  }, [userBanks, setValue]);
  useEffect(() => {
    if (loggedInUser?.id) {
      setValue("served_by_id", loggedInUser.id, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [loggedInUser, setValue]);
  useEffect(() => {
    if (watch("supplier_id")) {
      const formatNum = (val) => parseFloat(val || 0).toFixed(2);

      const specificProduct = watch("selected_product");
      const produceDisplay =
        watch("product_type_id") == "9" && specificProduct
          ? `${selectedProduct?.name} (${specificProduct})`
          : selectedProduct?.name;

      setData((prev) => ({
        ...prev,
        name: selectedSupplier?.display_name,
        served_by: selectedHandler?.display_name,
        produce: produceDisplay,
        phonenumber: selectedSupplier?.contact_phonenumber,
        weight: formatNum(watchedWeight),
        cost: formatNum(watchedCost),
        total: thousands_separators(totalCost.toFixed(2)),
        date: format(startDate, "yyyy-MM-dd"),
        lpo_date: format(startDate, "yyyyMMddHHmm"),
        location: selectedSupplier?.location,
        payment_name: getValues("payment_name"),
        payment_number: getValues("payment_number"),
      }));
    }
  }, [
    selectedHandler,
    selectedProduct,
    selectedSupplier,
    watchedWeight,
    watchedCost,
    totalCost,
    startDate,
    getValues,
    watch,
  ]);

  const sendEmail = () => {
    emailjs
      .send("supplier_sourcing", "supplier_sourcing", data)
      .then(() => toast({ title: "Email sent successful.", status: "success" }))
      .catch(() => toast({ title: "Unable to send Email", status: "error" }));
  };

  const { mutate, isLoading } = useMutation(
    (payload) => axios.post(`${SOURCING_PRODUCTS}`, payload),
    {
      onSuccess: (response) => {
        toast({ title: "Sourcing successful", status: "success" });
        if (data.name) sendEmail();
        reset();
        history.push({
          pathname: "/success",
          state: response?.data?.returned_resultset,
        });
      },
      onError: () => history.push("/error"),
    },
  );

  const onSubmit = (values) => {
    const now = new Date();
    const generatedLPO = format(now, "yyyyMMddHHmm");

    const payload = {
      ...values,
      notes:
        values.product_type_id === "9" || values.product_type_id === "3"
          ? `Product: ${values.selected_product}. ${values.notes || ""}`
          : values.notes,
      cost_per_kg: parseFloat(values.cost_per_kg || 0).toFixed(2),
      total_net_weight: parseFloat(values.total_net_weight || 0).toFixed(2),
      male_casuals: parseInt(values.male_casuals || 0),
      female_casuals: parseInt(values.female_casuals || 0),
      harvest_ready_date: format(harvestDate, "yyyy-MM-dd"),
      lpo_number: generatedLPO,
    };

    setData((prev) => ({
      ...prev,
      lpo_number: generatedLPO,
    }));

    [
      "cost",
      "weight",
      "name",
      "served_by",
      "phonenumber",
      "payment_number",
      "payment_name",
      "selected_product",
    ].forEach((k) => delete payload[k]);

    confirmAlert({
      title: "Confirm Sourcing Details",
      message: "Verify the details below before submitting.",
      buttons: [
        { label: "Confirm", onClick: () => mutate(payload) },
        { label: "Cancel" },
      ],
      childrenElement: () => (
        <List spacing={3}>
          <ListItem>Location: {values.address}</ListItem>
          <ListItem>
            Produce:{" "}
            {values.product_type_id == "9" || values.product_type_id == "3"
              ? `${selectedProduct?.name} (${values.selected_product})`
              : data.produce}
          </ListItem>

          <ListItem>Supplier: {selectedSupplier?.display_name}</ListItem>
          <ListItem>Cost Per Kg: {parseFloat(watchedCost).toFixed(2)}</ListItem>
          <ListItem>
            Total Weight: {parseFloat(watchedWeight).toFixed(2)}
          </ListItem>
          <ListItem>Served by: {selectedHandler?.display_name}</ListItem>

          <ListItem>
            Total: {thousands_separators(totalCost.toFixed(2))}
          </ListItem>
          <ListItem>Date: {format(startDate, "yyyy-MM-dd HH:mm")}</ListItem>
        </List>
      ),
    });
  };

  return (
    <form
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingBottom: "2rem",
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <VStack spacing={4} marginTop={30} align="stretch" width="80%">
        <FormControl isInvalid={errors?.name?.message}>
          <FormLabel>Supplier's Name</FormLabel>
          <Select
            placeholder="Select agent..."
            name="supplier_id"
            focusBorderColor="brand.800"
            {...register("supplier_id", { required: "Supplier is required" })}
          >
            {supplier
              ?.sort((a, b) => a.display_name.localeCompare(b.display_name))
              .map((supplier) => (
                <option key={supplier.id} value={supplier?.id}>
                  {supplier?.display_name}
                </option>
              ))}
          </Select>

          <FormErrorMessage>{errors?.supplier_id?.message}</FormErrorMessage>
        </FormControl>
        {selectedSupplier ? (
          <FormControl>
            <FormLabel>Location</FormLabel>
            <Input
              focusBorderColor="brand.800"
              disabled
              name="location"
              value={selectedSupplier?.location}
              placeholder="Enter location of the supplier"
              type="text"
            />
          </FormControl>
        ) : null}

        <FormControl isInvalid={errors?.product_type_id?.message}>
          <FormLabel>Produce Type</FormLabel>
          <Select
            placeholder="Select..."
            name="product_type_id"
            focusBorderColor="brand.800"
            {...register("product_type_id", {
              required: "Produce type is required",
            })}
          >
            {productTypes
              ?.filter((prod) => prod.id !== 4)
              ?.map((prod) => (
                <option key={prod.id} value={prod?.id}>
                  {prod?.name}
                </option>
              ))}
          </Select>

          <FormErrorMessage>
            {errors?.product_type_id?.message}
          </FormErrorMessage>
        </FormControl>

        {Number(watch("product_type_id")) === 9 && (
          <FormControl mt={4} isInvalid={errors?.selected_product}>
            <FormLabel>Specific Product</FormLabel>
            <Select
              placeholder="Select Product..."
              {...register("selected_product", {
                required: "Specific product is required for Chicken parts",
              })}
            >
              {chickenPartsProducts.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>
              {errors?.selected_product?.message}
            </FormErrorMessage>
          </FormControl>
        )}
        {Number(watch("product_type_id")) === 3 && (
          <FormControl mt={4} isInvalid={errors?.selected_product}>
            <FormLabel>Selected Product</FormLabel>
            <Select
              placeholder="Select Product..."
              {...register("selected_product", {
                required: "Specific product is required for other type",
              })}
            >
              {othertypeProducts.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>
              {errors?.selected_product?.message}
            </FormErrorMessage>
          </FormControl>
        )}

        <FormControl isInvalid={errors?.quality_score?.message}>
          <FormLabel>Quality score</FormLabel>
          <Select
            placeholder="Select..."
            name="quality_score"
            focusBorderColor="brand.800"
            {...register("quality_score", {
              required: "Please provide a quality score",
            })}
          >
            <option value="5">Very Good</option>
            <option value="4">Good</option>
            <option value="3">Average</option>
            <option value="2">Bad</option>
            <option value="1">Very Bad</option>
          </Select>

          <FormErrorMessage>{errors?.quality_score?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors?.cost_per_kg}>
          <FormLabel>Cost Per Kg</FormLabel>
          <Controller
            control={control}
            name="cost_per_kg"
            rules={{ required: "Cost is required" }}
            render={({ field: { onChange, value, ref } }) => (
              <NumberInput
                min={0}
                value={value}
                onChange={(val) => onChange(val)}
              >
                <NumberInputField
                  ref={ref}
                  placeholder="Enter cost per KG"
                  focusBorderColor="brand.800"
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          />
          <FormErrorMessage>{errors?.cost_per_kg?.message}</FormErrorMessage>
        </FormControl>

        {/* write new commented code here */}
        <FormControl isInvalid={!!errors?.total_net_weight}>
          <FormLabel>Total Net Weight</FormLabel>
          <Controller
            control={control}
            name="total_net_weight"
            rules={{ required: "Net weight is required" }}
            render={({ field: { onChange, value, ref } }) => (
              <NumberInput
                min={0}
                value={value}
                onChange={(valString) => onChange(valString)}
              >
                <NumberInputField
                  ref={ref}
                  placeholder="Enter total net weight"
                  focusBorderColor="brand.800"
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          />
          <FormErrorMessage>
            {errors?.total_net_weight?.message}
          </FormErrorMessage>
          <FormHelperText>
            Total ksh. {thousands_separators(totalCost)}
          </FormHelperText>
        </FormControl>
        <FormControl isInvalid={errors?.male_casuals?.message}>
          <FormLabel>No of male casuals</FormLabel>
          <NumberInput min={0}>
            <NumberInputField
              focusBorderColor="brand.800"
              name="male_casuals"
              {...register("male_casuals", {
                required: "Number of male casuals is required",
              })}
            />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormErrorMessage>
            {errors?.total_net_weight?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors?.female_casuals?.message}>
          <FormLabel>No of female casuals</FormLabel>
          <NumberInput min={0}>
            <NumberInputField
              focusBorderColor="brand.800"
              name="female_casuals"
              {...register("female_casuals", {
                required: "Number of female casuals is required",
              })}
            />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormErrorMessage>
            {errors?.total_net_weight?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors?.served_by_id?.message}>
          <FormLabel>Sourced By</FormLabel>
          <Select
            placeholder="Select..."
            name="served_by_id"
            focusBorderColor="brand.800"
            {...register("served_by_id", {
              required: "Served By is required",
            })}
          >
            {handlers
              ?.filter((handler) => handler.active)
              ?.map((user) => (
                <option key={user.id} value={user?.id}>
                  {user?.name}
                </option>
              ))}
          </Select>

          <FormErrorMessage>{errors?.served_by_id?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors?.date?.message}>
          <FormLabel>Date</FormLabel>
          <Datepicker
            startDate={startDate}
            setStartDate={setStartDate}
            name="date"
          />

          <FormErrorMessage>{errors?.date?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors?.harvest_ready_date?.message}>
          <FormLabel>Next Ready Harvest Date</FormLabel>
          <Datepicker
            startDate={harvestDate}
            setStartDate={setHarvestDate}
            name="harvest_ready_date"
          />

          <FormErrorMessage>{errors?.date?.message}</FormErrorMessage>
        </FormControl>
        <Heading size="sm">Payment Details</Heading>
        <VStack>
          <FormControl>
            <FormLabel>Payment Mode</FormLabel>
            <Select
              placeholder="Select..."
              name="payment_name"
              focusBorderColor="brand.800"
              {...register("payment_name")}
            >
              {banks?.map((bank) => (
                <option key={bank.id} value={bank?.name}>
                  {bank?.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Phone / Account Number</FormLabel>
            <Input
              name="payment_number"
              focusBorderColor="brand.800"
              placeholder="Enter phone number"
              type="text"
              {...register("payment_number")}
            />
          </FormControl>
        </VStack>
        <Input
          style={{ opacity: 0 }}
          focusBorderColor="brand.800"
          name="total"
          type="text"
          value={totalCost.toFixed(3)}
        />
        <HStack>
          <Button
            onClick={handleSubmit(onSubmit)}
            width="50%"
            bg="brand.900"
            isLoading={isLoading}
            colorScheme="purple"
          >
            Submit
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};

export default Form;
